import { CircularIconButton } from '#app/components/Buttons/CircularIconButton.js';
import { Card as CardComponent } from '#app/components/Card/src/Card.js';
import { revealLeftToRight } from '#app/components/ContraMagic/animations.js';
import { Link, type LinkProps } from '#app/components/Link/index.js';
import { Text } from '#app/components/Primitives/Text.js';
import {
  MOBILE_PROJECT_COVER_WIDTH,
  PROJECT_COVER_WIDTH,
} from '#app/components/ProjectCover/constants.js';
import { MAX_MAIN_LAYOUT_SIZE } from '#app/layouts/MainLayout/MainLayout.js';
import { css, styled } from 'styled-components';

const MOBILE_PROJECT_CARD_PADDING = 16;

type CardProps = {
  disabled?: boolean;
  empty?: boolean;
  isDragging?: boolean;
  isHighlighted?: boolean;
  isInteractive?: boolean;
  startRevealProjectAnimation?: boolean;
};

export const Card = styled(CardComponent).attrs({
  removeBoxShadow: true,
})<CardProps>`
  ${({
    disabled = false,
    empty = false,
    isDragging = false,
    startRevealProjectAnimation = false,
    theme,
  }) => css`
    height: auto;
    width: 100%;
    max-width: ${MOBILE_PROJECT_COVER_WIDTH +
    MOBILE_PROJECT_CARD_PADDING * 2}px;
    border-radius: 20px;
    padding: 16px;
    margin: 0 auto;
    position: relative;

    ${theme.mediaQueries.md} {
      height: 100%;
    }

    ${theme.mediaQueries.lg} {
      height: auto;
    }

    ${theme.mediaQueries.xl} {
      padding: 30px;
      margin: unset;
      max-width: unset;
    }

    border: 1px solid ${theme.colorsV2.gray30};

    ${isDragging &&
    css`
      box-shadow: 0 0 20px rgba(0, 0, 0, 0.08);
    `}

    ${disabled &&
    css`
      opacity: 0.3;
    `}

    ${empty &&
    css`
      height: 244px;

      ${theme.mediaQueries.xl} {
        height: 244px;
      }
    `}

    ${startRevealProjectAnimation
      ? css`
          animation: ${revealLeftToRight} 4s;
        `
      : undefined}
  `}
`;

export const Body = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    height: inherit;

    ${theme.mediaQueries.xl} {
      flex-direction: row;
    }
  `}
`;

export const Cover = styled(Link)`
  ${({ theme }) => css`
    display: flex;
    margin-bottom: 12px;
    border-radius: 10px;
    max-width: ${MOBILE_PROJECT_COVER_WIDTH}px;
    width: 100%;

    ${theme.mediaQueries.xl} {
      max-width: ${PROJECT_COVER_WIDTH}px;
      margin-right: 20px;
      margin-bottom: unset;
    }
  `}
`;

type DetailsProps = {
  addEditButtonPadding?: boolean;
  isGuideProject?: boolean;
  preview?: boolean;
};

export const Details = styled.div<DetailsProps>`
  ${({
    addEditButtonPadding = false,
    isGuideProject = false,
    preview = false,
    theme,
  }) => css`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: inherit;

    & > * + * {
      margin-top: 16px;
    }

    ${preview &&
    css`
      & > * + * {
        margin-top: 8px;
      }
    `}

    ${isGuideProject &&
    css`
      & > * + * {
        margin-top: 16px;
      }

      & > :nth-child(2) {
        margin-top: 4px;
      }
    `}

    ${addEditButtonPadding &&
    css`
      ${theme.mediaQueries.xl} {
        padding-right: 32px;
      }
    `}
  `}
`;

export const Title = styled(Text).attrs({
  as: Link,
  textStyle: 'fifthLevelHeadline',
})<LinkProps>`
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const Description = styled(Text).attrs({
  textStyle: 'bodySmall',
})<{ truncateText?: boolean }>`
  ${({ theme, truncateText }) => css`
    margin-top: 8px;
    color: ${theme.colorsV2.gray70};
    word-break: break-word;

    ${truncateText
      ? css`
          @media screen and (max-width: ${MAX_MAIN_LAYOUT_SIZE}px) {
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
        `
      : undefined}
  `}
`;

export const CardSortHandle = styled.div`
  ${({ theme }) => css`
    position: absolute;
    top: 8px;
    left: 8px;
    padding: 8px 4px;
    z-index: 1;
    margin: 12px;
    border-radius: 7px;
    cursor: move;
    touch-action: none;
    background-color: rgba(19, 19, 19, 0.4);
    color: ${theme.colorsV2.white};

    ${theme.mediaQueries.xl} {
      top: -4px;
      left: -8px;
      background-color: transparent;
      color: ${theme.colorsV2.black};
    }
  `}
`;

export const Button = styled.div`
  ${({ theme }) => css`
    position: absolute;
    top: 20px;
    right: 20px;
    z-index: 1;

    ${theme.mediaQueries.xl} {
      top: 12px;
      right: 12px;
    }
  `}
`;

export const MenuButton = styled(CircularIconButton)`
  ${({ theme }) => css`
  border: none;
  background-color: rgba(19, 19, 19, 0.4);
  color: ${theme.colorsV2.white};

  ${theme.mediaQueries.xl} {
    background-color: transparent;
    color: ${theme.colorsV2.black};
`}
`;

export const GuideTitle = styled(Text).attrs({ textStyle: 'bodySmall' })`
  ${({ theme }) => css`
    color: ${theme.colorsV2.gray60};
  `}
`;
